import { supabase } from './supabase';

export interface ScannedDocument {
  id: string;
  name: string;
  imageData: string;
  timestamp: Date;
  category: string;
  tags: string[];
  description?: string;
  file_size?: number;
  mime_type?: string;
  folder_path?: string;
  source_module?: string;
  shared_with?: string[];
}

export interface SaveDocumentParams {
  name: string;
  imageData: string;
  category: string;
  tags: string[];
  description?: string;
  folder_path?: string;
}

class ScannerService {
  private async uploadImageToStorage(imageData: string, fileName: string): Promise<string> {
    try {
      // Convert base64 to blob
      const base64Response = await fetch(imageData);
      const blob = await base64Response.blob();
      
      // Create file name with timestamp
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      const fileExtension = 'jpg';
      const fullFileName = `${fileName}_${timestamp}.${fileExtension}`;
      
      // Upload to Supabase Storage
      const { data, error } = await supabase.storage
        .from('my-files')
        .upload(`scanned-documents/${fullFileName}`, blob, {
          contentType: 'image/jpeg',
          cacheControl: '3600'
        });

      if (error) {
        throw new Error(`Upload failed: ${error.message}`);
      }

      // Get public URL
      const { data: urlData } = supabase.storage
        .from('my-files')
        .getPublicUrl(`scanned-documents/${fullFileName}`);

      return urlData.publicUrl;
    } catch (error) {
      console.error('Upload error:', error);
      throw new Error('Failed to upload image to storage');
    }
  }

  async saveScannedDocument(params: SaveDocumentParams): Promise<ScannedDocument> {
    try {
      const {
        name,
        imageData,
        category,
        tags,
        description,
        folder_path = 'scanned-documents'
      } = params;

      // Upload image to storage
      const fileUrl = await this.uploadImageToStorage(imageData, name);

      // Calculate file size (approximate from base64)
      const fileSize = Math.round((imageData.length * 3) / 4);

      // Create document record in database
      const { data: document, error } = await supabase
        .from('my_files')
        .insert({
          title: name,
          file_name: `${name}.jpg`,
          file_path: fileUrl, // Use file_path instead of file_url
          file_size: fileSize,
          mime_type: 'image/jpeg',
          category: category,
          tags: tags,
          description: description,
          folder_path: folder_path,
          source_module: 'scanner',
          shared_with: []
        })
        .select()
        .single();

      if (error) {
        throw new Error(`Database error: ${error.message}`);
      }

      return {
        id: document.id,
        name: document.title,
        imageData: imageData,
        timestamp: new Date(document.created_at),
        category: document.category,
        tags: document.tags || [],
        description: document.description,
        file_size: document.file_size,
        mime_type: document.mime_type,
        folder_path: document.folder_path,
        source_module: document.source_module,
        shared_with: document.shared_with || []
      };
    } catch (error) {
      console.error('Save document error:', error);
      throw new Error('Failed to save scanned document');
    }
  }

  async getScannedDocuments(): Promise<ScannedDocument[]> {
    try {
      const { data: documents, error } = await supabase
        .from('my_files')
        .select('*')
        .eq('source_module', 'scanner')
        .order('created_at', { ascending: false });

      if (error) {
        throw new Error(`Database error: ${error.message}`);
      }

      return documents.map(doc => ({
        id: doc.id,
        name: doc.title,
        imageData: doc.file_path, // This will be the URL, not base64
        timestamp: new Date(doc.created_at),
        category: doc.category,
        tags: doc.tags || [],
        description: doc.description,
        file_size: doc.file_size,
        mime_type: doc.mime_type,
        folder_path: doc.folder_path,
        source_module: doc.source_module,
        shared_with: doc.shared_with || []
      }));
    } catch (error) {
      console.error('Get documents error:', error);
      throw new Error('Failed to retrieve scanned documents');
    }
  }

  async deleteScannedDocument(documentId: string): Promise<void> {
    try {
      // Get document info first
      const { data: document, error: fetchError } = await supabase
        .from('my_files')
        .select('file_path, file_name')
        .eq('id', documentId)
        .single();

      if (fetchError) {
        throw new Error(`Document not found: ${fetchError.message}`);
      }

      // Delete from storage
      if (document.file_path) {
        const filePath = document.file_path.split('/').pop();
        if (filePath) {
          const { error: storageError } = await supabase.storage
            .from('my-files')
            .remove([`scanned-documents/${filePath}`]);

          if (storageError) {
            console.warn('Storage deletion failed:', storageError);
          }
        }
      }

      // Delete from database
      const { error: deleteError } = await supabase
        .from('my_files')
        .delete()
        .eq('id', documentId);

      if (deleteError) {
        throw new Error(`Database deletion failed: ${deleteError.message}`);
      }
    } catch (error) {
      console.error('Delete document error:', error);
      throw new Error('Failed to delete scanned document');
    }
  }

  async updateScannedDocument(
    documentId: string, 
    updates: Partial<Pick<ScannedDocument, 'name' | 'category' | 'tags' | 'description'>>
  ): Promise<ScannedDocument> {
    try {
      const { data: document, error } = await supabase
        .from('my_files')
        .update({
          title: updates.name,
          category: updates.category,
          tags: updates.tags,
          description: updates.description
        })
        .eq('id', documentId)
        .select()
        .single();

      if (error) {
        throw new Error(`Update failed: ${error.message}`);
      }

      return {
        id: document.id,
        name: document.title,
        imageData: document.file_path,
        timestamp: new Date(document.created_at),
        category: document.category,
        tags: document.tags || [],
        description: document.description,
        file_size: document.file_size,
        mime_type: document.mime_type,
        folder_path: document.folder_path,
        source_module: document.source_module,
        shared_with: document.shared_with || []
      };
    } catch (error) {
      console.error('Update document error:', error);
      throw new Error('Failed to update scanned document');
    }
  }

  // Helper method to convert image data to blob for download
  async downloadScannedDocument(imageData: string, fileName: string): Promise<void> {
    try {
      const base64Response = await fetch(imageData);
      const blob = await base64Response.blob();
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${fileName}.jpg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download error:', error);
      throw new Error('Failed to download document');
    }
  }

  // Get storage usage for scanned documents
  async getStorageUsage(): Promise<{ used: number; total: number }> {
    try {
      const { data: documents, error } = await supabase
        .from('my_files')
        .select('file_size')
        .eq('source_module', 'scanner');

      if (error) {
        throw new Error(`Database error: ${error.message}`);
      }

      const used = documents.reduce((total, doc) => total + (doc.file_size || 0), 0);
      const total = 100 * 1024 * 1024; // 100MB limit (example)

      return { used, total };
    } catch (error) {
      console.error('Storage usage error:', error);
      throw new Error('Failed to get storage usage');
    }
  }
}

export const scannerService = new ScannerService(); 